/**
 * Collection Navigation JavaScript
 * Handles auto-expand functionality and mobile collapsible menu
 */

class CollectionNavigation {
  constructor() {
    this.navigation = document.querySelector('.collection-navigation');
    this.details = document.querySelectorAll('.collection-nav-details');
    this.mobileBreakpoint = 990;

    if (!this.navigation) return;

    this.init();
  }

  init() {
    this.setupExpandableCollections();
    this.setupAutoExpand();
    this.setupMobileCollapsible();
    this.handleResize();

    window.addEventListener('resize', this.handleResize.bind(this));
  }

  setupExpandableCollections() {
    // No click handlers - only auto-expand when on collection page
  }

  setupAutoExpand() {
    const currentUrl = window.location.pathname;

    const activeLink = document.querySelector('.collection-nav-link.active');

    if (!activeLink) {
      return;
    }

    const currentHandle = this.getCurrentCollectionHandle(currentUrl, activeLink);

    if (!currentHandle) {
      return;
    }

    this.autoExpandParentOf(currentHandle);
    this.autoExpandCollection(currentHandle);
  }

  getCurrentCollectionHandle(url, activeLink) {
    if (activeLink && activeLink.dataset.collectionHandle) {
      return activeLink.dataset.collectionHandle;
    }

    const urlMatch = url.match(/\/collections\/([^\/\?]+)/);

    if (urlMatch) {
      try {
        return decodeURIComponent(urlMatch[1]);
      } catch (error) {
        return urlMatch[1];
      }
    }

    if (activeLink && activeLink.href) {
      const hrefMatch = activeLink.href.match(/\/collections\/([^\/\?]+)/);

      if (hrefMatch) {
        try {
          return decodeURIComponent(hrefMatch[1]);
        } catch (error) {
          return hrefMatch[1];
        }
      }
    }

    return null;
  }

  autoExpandParentOf(childHandle) {
    const currentLink = document.querySelector(`[data-collection-handle="${childHandle}"]`);

    if (!currentLink) return;

    const parentChain = this.buildParentChain(childHandle);

    parentChain.forEach((parentHandle) => {
      this.expandCollection(parentHandle);
    });
  }

  buildParentChain(collectionHandle) {
    const parentChain = [];
    const visitedHandles = new Set();
    let currentHandle = collectionHandle;

    while (currentHandle) {
      if (visitedHandles.has(currentHandle)) {
        break;
      }

      visitedHandles.add(currentHandle);

      const currentLink = document.querySelector(`[data-collection-handle="${currentHandle}"]`);

      if (!currentLink) break;

      const parentHandle = currentLink.dataset.parentCollection;

      if (parentHandle) {
        parentChain.unshift(parentHandle);
        currentHandle = parentHandle;
      } else {
        break;
      }
    }

    return parentChain;
  }

  expandCollection(collectionHandle) {
    const subcategoriesContainer = document.querySelector(`[data-collection-id="${collectionHandle}"]`);

    if (subcategoriesContainer) {
      subcategoriesContainer.style.display = 'block';

      const collectionLink = document.querySelector(`[data-collection-handle="${collectionHandle}"]`);

      if (collectionLink) {
        collectionLink.classList.add('expanded');
      }
    }
  }

  autoExpandCollection(currentHandle) {
    this.expandCollection(currentHandle);
  }

  setupAccordion() {
    this.details.forEach((detail) => {
      const summary = detail.querySelector('.collection-nav-summary');

      if (summary) {
        summary.addEventListener('click', (event) => {
          const isChevron = event.target.closest('svg');

          if (isChevron) {
            event.preventDefault();
            this.toggleAccordion(detail);
          }
        });
      }
    });

    const level3Details = document.querySelectorAll('.collection-nav-level3');

    level3Details.forEach((detail) => {
      const summary = detail.querySelector('.collection-nav-summary-level3');

      if (summary) {
        summary.addEventListener('click', (event) => {
          const isChevron = event.target.closest('svg');

          if (isChevron) {
            event.preventDefault();
            this.toggleAccordion(detail);
          }
        });
      }
    });
  }

  toggleAccordion(detail) {
    const isOpen = detail.hasAttribute('open');

    if (isOpen) {
      detail.removeAttribute('open');
    } else {
      detail.setAttribute('open', '');
    }

    const subcategories = detail.querySelector('.collection-nav-subcategories');

    if (subcategories) {
      if (isOpen) {
        subcategories.style.maxHeight = `${subcategories.scrollHeight}px`;

        setTimeout(() => {
          subcategories.style.maxHeight = '0';
        }, 10);
      } else {
        subcategories.style.maxHeight = `${subcategories.scrollHeight}px`;

        setTimeout(() => {
          subcategories.style.maxHeight = 'none';
        }, 300);
      }
    }
  }

  setupMobileCollapsible() {
    const header = this.navigation.querySelector('.collection-nav-header');
    const content = this.navigation.querySelector('.collection-nav-content');

    if (!header || !content) return;

    header.addEventListener('click', () => {
      this.toggleMobileNavigation();
    });

    this.navigation.querySelectorAll('.collection-nav-link').forEach((link) => {
      link.addEventListener('click', () => {
        this.closeMobileNavigation();
      });
    });
  }

  isMobile() {
    return window.matchMedia(`(max-width: ${this.mobileBreakpoint - 1}px)`).matches;
  }

  openMobileNavigation() {
    if (!this.isMobile()) return;

    this.navigation.classList.add('is-open');
  }

  closeMobileNavigation() {
    if (!this.isMobile()) return;

    this.navigation.classList.remove('is-open');
  }

  toggleMobileNavigation() {
    if (!this.isMobile()) return;

    this.navigation.classList.toggle('is-open');
  }

  handleResize() {
    if (this.isMobile()) {
      this.navigation.classList.remove('mobile-drawer', 'open');
      this.closeMobileNavigation();
      document.body.style.overflow = '';
    } else {
      this.navigation.classList.remove('is-open', 'mobile-drawer', 'open');
      document.body.style.overflow = '';
    }

    const overlay = document.querySelector('.collection-nav-overlay');

    if (overlay) {
      overlay.remove();
    }

    const trigger = document.querySelector('.collection-nav-mobile-trigger');

    if (trigger) {
      trigger.style.display = 'none';
    }

    const closeButton = this.navigation.querySelector('.collection-nav-close');

    if (closeButton) {
      closeButton.remove();
    }
  }

  refresh() {
    this.details = document.querySelectorAll('.collection-nav-details');
    this.setupAccordion();
    this.setupAutoExpand();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CollectionNavigation();
});

document.addEventListener('shopify:section:load', () => {
  new CollectionNavigation();
});